/**
 * Sprint T-15 · Founder Security Center · Audit Events tab.
 *
 * Consumes ONLY /api/superadmin/audit-events (existente · S-4).
 * Filtros: action · actor_email · organization_id · only_failures · pagination.
 */
import React, { useMemo, useState } from "react";
import { Filter, ChevronLeft, ChevronRight, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useAuditEvents } from "../../hooks/useSecurity";

const PAGE_SIZE = 50;

const fmtIso = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return iso;
  }
};

export default function AuditEventsTab() {
  const [filters, setFilters] = useState({
    action: "",
    actor_email: "",
    organization_id: "",
    only_failures: false,
  });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(0);

  const query = useMemo(() => ({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    action: applied.action || undefined,
    actor_email: applied.actor_email || undefined,
    organization_id: applied.organization_id || undefined,
    only_failures: applied.only_failures || undefined,
  }), [applied, page]);

  const events = useAuditEvents(query);

  const data = events.data;
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const breakdown = data?.breakdown_top10 || [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setApplied(filters);
  };

  const resetFilters = () => {
    const empty = { action: "", actor_email: "", organization_id: "", only_failures: false };
    setFilters(empty);
    setApplied(empty);
    setPage(0);
  };

  return (
    <section className="space-y-4" data-testid="security-audit-tab">
      <WidgetCard
        title="Filtros"
        description="Combina acción, actor, organización y/o solo fallos. Limit 50/página · retención 365 días."
        citations={["/api/superadmin/audit-events"]}
        testId="widget-audit-filters"
      >
        <form
          onSubmit={applyFilters}
          className="grid grid-cols-1 gap-3 md:grid-cols-5"
          data-testid="audit-filters-form"
        >
          <div className="md:col-span-1">
            <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Acción</label>
            <input
              data-testid="audit-filter-action"
              type="text"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              placeholder="auth.login.fail"
              className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
            />
          </div>
          <div className="md:col-span-1">
            <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Email actor</label>
            <input
              data-testid="audit-filter-email"
              type="email"
              value={filters.actor_email}
              onChange={(e) => setFilters((f) => ({ ...f, actor_email: e.target.value }))}
              placeholder="user@example.com"
              className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
            />
          </div>
          <div className="md:col-span-1">
            <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Organization ID</label>
            <input
              data-testid="audit-filter-org"
              type="text"
              value={filters.organization_id}
              onChange={(e) => setFilters((f) => ({ ...f, organization_id: e.target.value }))}
              placeholder="org-uuid"
              className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs" data-testid="audit-filter-failures-label">
              <input
                data-testid="audit-filter-failures"
                type="checkbox"
                checked={filters.only_failures}
                onChange={(e) => setFilters((f) => ({ ...f, only_failures: e.target.checked }))}
                className="h-4 w-4"
              />
              <span>Sólo fallos (<code className="font-mono">ok=false</code>)</span>
            </label>
          </div>
          <div className="md:col-span-1 flex items-end gap-2">
            <button
              data-testid="audit-apply-btn"
              type="submit"
              className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600"
            >
              <Filter className="h-3 w-3" /> Aplicar
            </button>
            <button
              data-testid="audit-reset-btn"
              type="button"
              onClick={resetFilters}
              className="rounded-md border border-[var(--ag-border)] px-3 py-1.5 text-xs hover:bg-[var(--ag-surface-2)]"
            >
              Limpiar
            </button>
            <button
              data-testid="audit-refresh-btn"
              type="button"
              onClick={() => events.refetch()}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--ag-border)] px-3 py-1.5 text-xs hover:bg-[var(--ag-surface-2)]"
              aria-label="Refrescar"
            >
              <RefreshCw className={`h-3 w-3 ${events.isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </form>
      </WidgetCard>

      {breakdown.length > 0 && (
        <WidgetCard
          title="Top acciones (breakdown del filtro actual)"
          description="Agregado backend · `$group` MongoDB top 10."
          citations={["/api/superadmin/audit-events.breakdown_top10"]}
          testId="widget-audit-breakdown"
        >
          <div className="flex flex-wrap gap-2" data-testid="audit-breakdown-list">
            {breakdown.map((b) => (
              <span
                key={b.action}
                data-testid={`audit-breakdown-${b.action}`}
                className="inline-flex items-center gap-2 rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 text-[10px] font-mono"
              >
                <span>{b.action}</span>
                <span className="rounded bg-[var(--ag-surface-2)] px-1.5 py-0.5 text-[9px]">{b.count}</span>
              </span>
            ))}
          </div>
        </WidgetCard>
      )}

      <WidgetCard
        title={`Eventos · ${total} resultados`}
        description={
          applied.only_failures
            ? "Filtrando solo eventos con resultado fail"
            : "Listado read-only · paginado 50/página · ordenado por timestamp desc"
        }
        citations={["audit_events @ MongoDB", "audit.AuditAction enum"]}
        testId="widget-audit-list"
      >
        {events.isLoading && <LoadingState />}
        {events.error && <EmptyState message="No se pudo cargar el listado." />}
        {data && items.length === 0 && (
          <EmptyState
            title="Sin eventos."
            message="No hay registros que coincidan con los filtros aplicados."
            testId="audit-empty"
          />
        )}
        {data && items.length > 0 && (
          <>
            <div className="-mx-1 overflow-x-auto">
              <table className="min-w-full text-xs" data-testid="audit-table">
                <thead className="border-b border-[var(--ag-border)] text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
                  <tr>
                    <th className="px-2 py-2 text-left">Hora</th>
                    <th className="px-2 py-2 text-left">Acción</th>
                    <th className="px-2 py-2 text-left">Actor</th>
                    <th className="px-2 py-2 text-left">IP</th>
                    <th className="px-2 py-2 text-left">Target</th>
                    <th className="px-2 py-2 text-left">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e, i) => (
                    <tr
                      key={`${e.ts_iso}-${i}`}
                      data-testid={`audit-row-${i}`}
                      className="border-b border-[var(--ag-border)]/60"
                    >
                      <td className="px-2 py-1.5 align-top font-mono text-[10px] text-[var(--ag-text-dim)]">
                        {fmtIso(e.ts_iso)}
                      </td>
                      <td className="px-2 py-1.5 align-top font-mono text-[11px]">{e.action}</td>
                      <td className="px-2 py-1.5 align-top">
                        <div className="truncate max-w-[180px]" title={e.actor_email}>
                          {e.actor_email || "—"}
                        </div>
                        {e.actor_id && (
                          <div className="text-[9px] text-[var(--ag-text-dim)] font-mono">{e.actor_id.slice(0, 8)}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-top font-mono text-[10px] text-[var(--ag-text-dim)]">
                        {e.ip || "—"}
                      </td>
                      <td className="px-2 py-1.5 align-top text-[10px] text-[var(--ag-text-dim)]">
                        {e.target_type ? (
                          <span>
                            {e.target_type}
                            {e.target_id && (
                              <span className="font-mono"> · {String(e.target_id).slice(0, 8)}</span>
                            )}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {e.ok === false ? (
                          <span className="inline-flex items-center gap-1 text-red-500" data-testid={`audit-result-fail-${i}`}>
                            <ShieldAlert className="h-3 w-3" /> FAIL
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-500" data-testid={`audit-result-ok-${i}`}>
                            <ShieldCheck className="h-3 w-3" /> OK
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-3 flex items-center justify-between gap-3 text-xs" data-testid="audit-pagination">
              <div className="text-[var(--ag-text-dim)]">
                Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="audit-page-prev"
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || events.isFetching}
                  className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)] disabled:opacity-30"
                >
                  <ChevronLeft className="h-3 w-3" /> Anterior
                </button>
                <span className="px-2 text-[10px] tabular-nums">
                  pág {page + 1} / {totalPages}
                </span>
                <button
                  data-testid="audit-page-next"
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= total || events.isFetching}
                  className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)] disabled:opacity-30"
                >
                  Siguiente <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </>
        )}
      </WidgetCard>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["audit_events 365d retention", "S-4 endpoint · sin cambios", "ai_platform_freeze=true"]} />
      </footer>
    </section>
  );
}
